import { MAX_UPLOAD_BYTES, formatMegabytes } from './firebaseUpload';

/**
 * Compression des PDF trop lourds pour les Security Rules Firebase.
 *
 * Motivation (dossier FR-103-ET, BH CAR AUDENGE, 2026-09) : les agences
 * scannent la déclaration d'achat ou le certificat de cession sur un
 * multifonction réglé en haute qualité et obtiennent un PDF de 20 à 90 Mo.
 * Firebase refuse tout ce qui atteint 15 Mo avec un `storage/unauthorized`
 * illisible, et l'agence redépose le même fichier en boucle : 58 tentatives
 * en une matinée pour BH CAR, 91 pour SimpliciCar Vannes.
 *
 * `getUploadRejectionReason` explique désormais le refus, mais expliquer ne
 * débloque pas : l'agence doit retourner au scanner. On rattrape donc le
 * fichier dans le navigateur avant l'envoi.
 *
 * Le PDF est rasterisé page par page puis réassemblé en JPEG. C'est destructif
 * pour un PDF vectoriel (le texte n'est plus sélectionnable), d'où le
 * déclenchement UNIQUEMENT au-delà de la limite Firebase : à cette taille le
 * document est toujours un scan, donc déjà une image. Un PDF natif signé
 * électroniquement pèse quelques centaines de kilo-octets et n'entre jamais
 * ici.
 *
 * Tout se passe côté navigateur : le fichier ne transite pas par le backend,
 * qui n'a ni à encaisser 90 Mo en mémoire ni à rasteriser pour toutes les
 * agences en même temps.
 */

/** Un palier de compression : résolution de rendu et qualité JPEG. */
export interface CompressionStep {
  /** Multiplicateur appliqué au viewport PDF (1.0 ≈ 72 dpi). */
  viewportScale: number;
  /** Qualité JPEG, entre 0 et 1. */
  jpegQuality: number;
}

/**
 * Paliers essayés dans l'ordre, du plus fidèle au plus agressif. On s'arrête
 * au premier qui passe sous la limite Firebase.
 *
 * Le plancher est à 1.2 (≈ 86 dpi) et pas plus bas : ces PDF repartent en OCR
 * (extraction carte grise, cross-check CTVO) et un Cerfa rendu trop petit
 * devient illisible pour le modèle comme pour l'humain qui relit.
 */
export const PDF_COMPRESSION_STEPS: readonly CompressionStep[] = [
  { viewportScale: 2.0, jpegQuality: 0.75 },
  { viewportScale: 1.5, jpegQuality: 0.65 },
  { viewportScale: 1.2, jpegQuality: 0.55 },
];

/**
 * Au-delà, on ne tente même pas : rasteriser un tel fichier ferait ramer ou
 * planter l'onglet de l'agence pour un résultat qui a toutes les chances de
 * rester hors limite. Le plus gros vu en production est un scan de 93,8 Mo.
 */
export const MAX_COMPRESSIBLE_BYTES = 120 * 1024 * 1024;

/**
 * Échec de compression dont le message est DESTINÉ À L'AGENCE. Il dit quoi
 * faire (rescanner moins finement), pas ce qui a planté.
 */
export class PdfCompressionError extends Error {
  constructor(message: string, public readonly fileName: string) {
    super(message);
    this.name = 'PdfCompressionError';
  }
}

/** Rend un PDF en JPEG à un palier donné et le réassemble en PDF. */
export type PdfRasterizer = (file: File, step: CompressionStep) => Promise<Blob>;

/**
 * Un PDF sous la limite passe tel quel : on ne dégrade jamais un fichier qui
 * serait accepté en l'état.
 */
export const needsPdfCompression = (file: File): boolean =>
  file.type === 'application/pdf' && file.size >= MAX_UPLOAD_BYTES;

const RESCAN_ADVICE = 'Rescannez-le en qualité standard (200 à 300 dpi), en noir et blanc si possible, puis redéposez-le.';

export interface CompressPdfOptions {
  /** Injecté par les tests ; par défaut, le rasteriseur navigateur. */
  rasterize?: PdfRasterizer;
  /** Appelé à chaque palier tenté, pour informer l'agence que ça travaille. */
  onAttempt?: (step: CompressionStep, attemptIndex: number, totalAttempts: number) => void;
}

/**
 * Ramène un PDF sous `MAX_UPLOAD_BYTES`, ou lève une `PdfCompressionError`
 * dont le message peut être affiché tel quel.
 *
 * Retourne le fichier d'origine inchangé s'il passe déjà.
 */
export const compressPdfToFit = async (file: File, options: CompressPdfOptions = {}): Promise<File> => {
  if (!needsPdfCompression(file)) {
    return file;
  }

  if (file.size > MAX_COMPRESSIBLE_BYTES) {
    throw new PdfCompressionError(
      `Fichier beaucoup trop volumineux (${formatMegabytes(file.size)}). ${RESCAN_ADVICE}`,
      file.name,
    );
  }

  const rasterize = options.rasterize ?? (await loadBrowserRasterizer());

  let smallest: Blob | null = null;

  for (const [index, step] of PDF_COMPRESSION_STEPS.entries()) {
    options.onAttempt?.(step, index, PDF_COMPRESSION_STEPS.length);

    let candidate: Blob;
    try {
      candidate = await rasterize(file, step);
    } catch {
      // Un PDF corrompu ou protégé par mot de passe fait échouer le rendu.
      // Inutile d'insister aux paliers suivants, ils utilisent le même moteur.
      throw new PdfCompressionError(
        `Ce PDF n'a pas pu être traité (${formatMegabytes(file.size)}). ${RESCAN_ADVICE}`,
        file.name,
      );
    }

    if (candidate.size < MAX_UPLOAD_BYTES) {
      return new File([candidate], file.name, { type: 'application/pdf' });
    }
    if (!smallest || candidate.size < smallest.size) {
      smallest = candidate;
    }
  }

  throw new PdfCompressionError(
    `Ce document reste trop volumineux après compression ` +
      `(${formatMegabytes(smallest?.size ?? file.size)}, maximum ${formatMegabytes(MAX_UPLOAD_BYTES)}). ` +
      `${RESCAN_ADVICE}`,
    file.name,
  );
};

/**
 * Le moteur de rendu (pdfjs + pdf-lib, ~1 Mo) n'est téléchargé qu'au premier
 * PDF hors limite. Les agences qui scannent correctement ne le paient jamais,
 * et le bundle initial reste inchangé.
 */
const loadBrowserRasterizer = async (): Promise<PdfRasterizer> => {
  const { rasterizePdfInBrowser } = await import('./pdfRasterizer');
  return rasterizePdfInBrowser;
};
